<template>
  <RevDrawer
    :back-button-label="i18n(translations.backButtonLabel)"
    :close-button-label="i18n(translations.closeButtonLabel)"
    data-test="modal-trade-in-au"
    :has-back-button
    :name="props.modalName"
    :title="modalTitle"
    @back="handleBack"
    @close="handleClose"
    @open="handleOpen"
  >
    <template #body>
      <div v-if="isLoading" class="mt-6 flex items-center justify-center">
        <RevLoadingScreen :text="spinnerLoadText" />
      </div>
      <div v-else>
        <div v-if="offer === undefined && question">
          <h3 class="text-action-default-hi heading-3">
            {{ question.title }}
          </h3>
          <RevInputSelect
            v-if="question.type === 'select'"
            :id="question.key"
            v-model="selectedValue"
            class="mt-4"
            :data-qa="question.key"
            :label="i18n(translations.selectOptionPlaceholder)"
            :options="selectOptions"
            @update:model-value="onSelect"
          />
          <div
            v-if="question.type === 'radioline'"
            class="grid grid-cols-1 gap-y-8 mt-4"
            :data-qa="question.key"
          >
            <div
              v-for="option in question.options"
              :key="option.value"
              class="w-full"
            >
              <RevPicker
                :id="option.value"
                alignment="left"
                class="h-full w-full"
                :data-qa="`${question.key}-${option.value}`"
                :description="option.description"
                :label="option.label"
                :model-value="selectedValue === option.value"
                @update:model-value="
                  handleRefreshRenewableQuestion(
                    question.key,
                    option.value,
                    question.lastQuestion,
                  )
                "
              />
            </div>
          </div>
          <div
            v-if="question.infoBlock"
            class="text-action-default-low body-2 my-8 flex flex-col gap-8"
          >
            <p v-for="text in question.infoBlock.paragraphs" :key="text">
              {{ text }}
            </p>
          </div>
        </div>
        <div v-else-if="offer" class="flex flex-col">
          <TradeInOffer :name="offer.productShortTitle" :price="offer.price" />
          <RevForm
            :initial-values="initialAcceptOfferValues"
            :validate
            @submit="acceptOffer"
          >
            <template #default="{ values, errors }">
              <template v-if="showEmailInput">
                <p class="text-static-default-hi body-1-bold mt-20">
                  {{ i18n(translations.emailRequestTitle) }}
                </p>
                <p class="text-static-default-mid body-2 mt-4">
                  {{ i18n(translations.applicationInstructionNotice) }}
                </p>
                <RevInputText
                  id="anonymousCustomerEmail"
                  v-model="
                    (values as typeof initialAcceptOfferValues)
                      .anonymousCustomerEmail
                  "
                  class="mt-20"
                  :error="
                    (errors as Record<string, string>).anonymousCustomerEmail
                  "
                  :icon="IconMail"
                  :label="i18n(translations.inputEmailLabel)"
                />
                <FormattedMessage
                  class="text-static-default-low caption mt-8"
                  :definition="translations.privacyPolicyNotice"
                  tag="p"
                >
                  <template #privacyPolicy>
                    <RevLink target="_blank" :to="privacyPolicyLink">
                      {{ i18n(translations.privacyPolicyLink) }}
                    </RevLink>
                  </template>
                </FormattedMessage>
              </template>
              <RevButton
                class="mt-20"
                data-qa="accept-offer"
                data-test="accept-offer"
                :disabled="
                  acceptOfferDisabled(
                    values as FormValues,
                    errors as Record<string, string>,
                  )
                "
                full-width="always"
                type="submit"
                variant="primary"
              >
                {{ i18n(translations.acceptOffer) }}
              </RevButton>
              <div
                v-if="!showEmailInput"
                class="text-static-default-mid caption mt-20 flex flex-col"
              >
                <p>{{ i18n(translations.applicationInstructionNotice) }}</p>
                <p>{{ i18n(translations.quoteValidityNotice) }}</p>
              </div>
            </template>
          </RevForm>
        </div>
        <div v-else>
          <div class="flex flex-col gap-y-20">
            <div class="flex flex-col items-center text-center">
              <IconTradeIn class="text-static-default-hi mb-8 h-48 w-48" />
              <div class="w-full">
                <h2 class="text-static-default-hi heading-1 text-center">
                  {{ i18n(translations.summaryTitle) }}
                </h2>
                <p class="text-static-default-hi caption text-center">
                  {{ i18n(translations.summaryText) }}
                </p>
              </div>
            </div>
            <div class="flex flex-col gap-12">
              <RevCard
                v-for="step in summarySteps"
                :key="step.title.id"
                class="rounded-md shadow-none py-10 px-20"
              >
                <div class="flex flex-col gap-6">
                  <p>
                    <span
                      class="text-static-default-mid label-medium-bold mr-11"
                      >{{ i18n(step.title) }}</span
                    >
                    <span class="text-static-default-hi body-1-bold">{{
                      i18n(step.subTitle)
                    }}</span>
                  </p>
                  <p class="text-static-default-low caption text-justify">
                    {{ i18n(step.text) }}
                  </p>
                </div>
              </RevCard>
            </div>
            <TradeInProviderInfo class="mt-8" />
            <div class="flex flex-col">
              <RevButton
                data-qa="start-trade-in"
                data-test="start-trade-in"
                full-width="always"
                variant="primary"
                @click="startTradeIn"
              >
                {{ i18n(translations.startTradeIn) }}
              </RevButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, nextTick, reactive, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDrawer } from '@ds/components/Drawer'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { RevPicker } from '@ds/components/Picker'
import { IconMail } from '@ds/icons/IconMail'
import { IconTradeIn } from '@ds/icons/IconTradeIn'

import type { Answer, Offer, Question } from '~/api/tradein-apac/types'
import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { REGEXP_VALID_EMAIL } from '~/constants/regex'
import { CMS } from '~/scopes/cms/routes-names'
import { LEGAL_PAGE_NAME } from '~/scopes/legal/constants'
import TradeInOffer from '~/scopes/tradein-apac/components/TradeInOffer/TradeInOffer.vue'
import TradeInProviderInfo from '~/scopes/tradein-apac/components/TradeInProviderInfo/TradeInProviderInfo.vue'
import { useTradeInApacOffer } from '~/scopes/tradein-apac/composables/useTradeInApacOffer'

import translations from './TradeInModal.translations'

export interface ModalProps {
  modalName: string
  intro?: string
  zone: string
}

const i18n = useI18n()
const logger = useLogger()
const $httpFetch = useHttpFetch()
const { openSuccessToast, openErrorToast } = useTheToast()
const { trackClick, trackSwapOffer } = useTracking()
const { isUserAuthenticated } = useUserStore()
const { countryCode } = useMarketplace().market
const pathParamMarketValue = countryCode.toLowerCase()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const privacyPolicyLink = resolveLocalizedRoute({
  name: CMS.LEGAL_PAGE,
  params: {
    pageName: LEGAL_PAGE_NAME.DATA_PROTECTION,
  },
})

const props = defineProps<ModalProps>()
const { setAcceptedOffer } = useTradeInApacOffer()
const question = ref<Question>()
const offer = ref<Offer>()

const modalTitle = i18n(translations.modalTitle)
const spinnerLoadText = i18n(translations.spinnerLoadText)

const answers = ref<Answer[]>([])
const previousQuestions = ref<Question[]>([])
const isLoading = ref(false)
let abortController: AbortController | undefined
const selectedValue = ref<string | null>(null)

type FormValues = {
  anonymousCustomerEmail: string
}

const initialAcceptOfferValues = reactive<FormValues>({
  anonymousCustomerEmail: '',
})

const isSwapAfterCheckoutEnabled =
  useRuntimeConfig().public.FF_TRADEIN_APAC_SWAP_AFTER_CHECKOUT?.includes(
    countryCode,
  ) ?? false

const showEmailInput = computed(
  () => !isUserAuthenticated.value && !isSwapAfterCheckoutEnabled,
)

const validate = computed(() =>
  showEmailInput.value
    ? makeValidate<FormValues>({
        anonymousCustomerEmail: [
          required(i18n(translations.emailRequiredError)),
          matchingRegExp(
            REGEXP_VALID_EMAIL,
            i18n(translations.emailInvalidError),
          ),
        ],
      })
    : makeValidate<FormValues>({}),
)

const summarySteps = [
  {
    title: translations.step1Title,
    subTitle: translations.step1SubTitle,
    text: translations.step1Text,
  },
  {
    title: translations.step2Title,
    subTitle: translations.step2SubTitle,
    text: translations.step2Text,
  },
  {
    title: translations.step3Title,
    subTitle: translations.step3SubTitle,
    text: translations.step3Text,
  },
]

const hasBackButton = computed(
  () =>
    (question.value !== undefined || offer.value !== undefined) &&
    (previousQuestions.value.length > 0 || !!props.intro),
)

function resetFunnel(): void {
  question.value = undefined
  offer.value = undefined
  answers.value = []
  previousQuestions.value = []
  selectedValue.value = null
}

function handleBack(): void {
  if (isLoading.value) {
    // The "Accept offer" request cannot be taken back once sent: ignore the back click
    if (offer.value) return

    // Still fetching the next question or offer: cancel the fetch,
    // then go back one step (this also removes the answer just added)
    abortController?.abort()
    isLoading.value = false
  }

  const previousQuestion = previousQuestions.value.pop()

  offer.value = undefined
  question.value = previousQuestion
  selectedValue.value = null
  answers.value = previousQuestion ? answers.value.slice(0, -1) : []
}

function handleClose() {
  resetFunnel()
}

const selectOptions = computed(
  () =>
    question.value?.options.map((option) => ({
      label: option.label,
      value: option.value,
    })) ?? [],
)

function startTradeIn(): void {
  trackClick({
    zone: props.zone,
    name: 'buyback_estimate',
  })
  void getQuestion()
}

async function getQuestion(): Promise<void> {
  abortController?.abort()
  const ctrl = new AbortController()
  abortController = ctrl

  try {
    isLoading.value = true
    selectedValue.value = null
    question.value = await $httpFetch(
      '/api/apac-trade-in/:market/v1/next-question',
      {
        method: 'POST',
        pathParams: {
          market: pathParamMarketValue,
        },
        body: {
          answers: answers.value,
        },
        signal: ctrl.signal,
      },
    )
  } catch (err) {
    // The user went back while this fetch was running: no error to handle
    if (ctrl.signal.aborted) return

    openErrorToast()
    logger.error('[TRADE-IN-AU][API] Error fetching funnel questions', {
      error: err as HttpApiError,
      owners: ['bot-squad-apac-front'],
    })
    handleBack()
  } finally {
    if (!ctrl.signal.aborted) isLoading.value = false
  }
}

async function getOffer(): Promise<void> {
  abortController?.abort()
  const ctrl = new AbortController()
  abortController = ctrl

  try {
    isLoading.value = true
    offer.value = await $httpFetch('/api/apac-trade-in/:market/v1/offer', {
      method: 'POST',
      pathParams: {
        market: pathParamMarketValue,
      },
      body: {
        answers: answers.value,
      },
      signal: ctrl.signal,
    })
  } catch (err) {
    // The user went back while this fetch was running: no error to handle
    if (ctrl.signal.aborted) return

    openErrorToast()
    logger.error('[TRADE-IN-AU][API] Error fetching offer', {
      error: err as HttpApiError,
      owners: ['bot-squad-apac-front'],
    })
    handleBack()
  } finally {
    if (!ctrl.signal.aborted) isLoading.value = false
  }
}

async function acceptOffer(values: FormValues): Promise<void> {
  if (!offer.value) return

  try {
    isLoading.value = true

    if (!isSwapAfterCheckoutEnabled) {
      await $httpFetch('/api/apac-trade-in/:market/v1/accept-offer', {
        method: 'POST',
        pathParams: {
          market: pathParamMarketValue,
        },
        body: {
          offer: offer.value,
          ...(isUserAuthenticated.value
            ? {}
            : { email: values.anonymousCustomerEmail }),
        },
      })
    }

    setAcceptedOffer(offer.value)
    trackSwapOffer({
      zone: props.zone,
      model: offer.value.productShortTitle,
      swap_estimation: parseInt(offer.value.price.amount, 10),
      price: offer.value.price.amount,
    })
    openSuccessToast({
      title: i18n(translations.successAcceptOfferTitle),
      content: i18n(translations.successAcceptOffer),
    })

    resetFunnel()
    closeModal(props.modalName)
  } catch (err) {
    openErrorToast()
    logger.error('[TRADE-IN-AU][API] Error accepting offer', {
      error: err as HttpApiError,
      owners: ['bot-squad-apac-front'],
    })
  } finally {
    isLoading.value = false
  }
}

function onSelect(event: unknown) {
  if (!question.value) return

  let extracted: string | null = null

  if (typeof event === 'string') {
    extracted = event
  } else if (typeof event === 'object' && event !== null && 'value' in event) {
    const maybeValue = event.value
    if (typeof maybeValue === 'string') {
      extracted = maybeValue
    }
  }

  if (!extracted) return

  handleRefreshRenewableQuestion(
    question.value.key,
    extracted,
    question.value.lastQuestion,
  )
}

async function handleRefreshRenewableQuestion(
  key: string,
  value: string,
  lastQuestion: boolean,
) {
  if (question.value) previousQuestions.value.push(question.value)
  answers.value.push({ key, value })

  if (lastQuestion === false) {
    await getQuestion()
  } else {
    await getOffer()
  }
}

function acceptOfferDisabled(
  values: FormValues,
  errors: Record<string, string>,
): boolean {
  if (!showEmailInput.value) return false

  return !values.anonymousCustomerEmail || !!errors.anonymousCustomerEmail
}

async function handleOpen() {
  // 'props.intro' is usually passed as a ref and updated just before opening the modal
  // but the value takes more time to be updated in 'props.intro'.
  await nextTick()

  if (!props.intro) {
    startTradeIn()
  }
}
</script>
